import { Component, inject, Input, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { KeyValue } from '@angular/common';
import { DEPOSITO_DATALIST_NAME } from '../../../../model/contants/datalists';
import { UdeuLocation } from '../../../../model/classes/udeu-record';
import { SMTDataListsService } from 'projects/alden-extension/src/lib/services/data-lists.service';
import { NodePaging } from '@alfresco/js-api';
import { AppStore, SnackbarWarningAction } from '@alfresco/aca-shared/store';
import { Store } from '@ngrx/store';
import { RecordService } from '../../../../services/record.service';
import { DEPOSITO_PROVISIONAL_FIELD } from 'projects/deusto-extension/src/lib/model/contants/udeu-record';
import { startWith } from 'rxjs/operators';

@Component({
  selector: 'alden-locations',
  templateUrl: './locations.component.html',
  styleUrls: ['./locations.component.scss']
})
export class LocationsComponent implements OnInit {
  @Input() group: FormGroup<any>;
  @Input() controlName: string;
  @Input() udeuLocations: UdeuLocation[];
  @Input() dataFormGroup: FormGroup<any>;

  private _formBuilder = inject(FormBuilder);
  formGroup: FormGroup<any>;

  depositOptions: KeyValue<string, string>[];

  isValidDocument: boolean;

  loading: boolean;
  loadingDocuments: boolean;
  disabled: boolean;

  constructor(private smtDataListsService: SMTDataListsService, private recordService: RecordService, protected store: Store<AppStore>) {}

  async ngOnInit(): Promise<void> {
    this.loading = true;
    this.disabled =this.group.get(this.controlName).disabled;

    this.formGroup = this.createForm();

    if(this.udeuLocations) {
      this.udeuLocations = this.udeuLocations.sort((a, b) => a.signatura.localeCompare(b.signatura));
      this.udeuLocations.forEach((udeuLocation) => {
        this.addLocationForm(udeuLocation);
      });
    }

    await this.smtDataListsService.getDataListValues(DEPOSITO_DATALIST_NAME).then((res) => this.depositOptions = res);
    
    this.formGroup.get('deposit')?.valueChanges.subscribe((value:any) => {if(value)this.depositChange()})

    this.dataFormGroup.get(DEPOSITO_PROVISIONAL_FIELD)?.valueChanges.pipe(startWith(this.dataFormGroup.get(DEPOSITO_PROVISIONAL_FIELD)?.value)).subscribe((value:any) => {
        this.formGroup.get('deposit')?.setValue(value);
    });

    this.formGroup.get('installationUnit').valueChanges.subscribe((value: any) => {
      value ? this.disable('signaturaDocE') : this.enable('signaturaDocE');
      this.formGroup.get('document').setValue('');
    });

    this.formGroup.get('document').valueChanges.subscribe((value: any) => {
      this.isValidDocument = value ? true : false;
    });

    this.formGroup.get('signaturaDocE').valueChanges.subscribe((value: any) => {
      if(value) {
        this.isValidDocument = true;
        this.disable('installationUnit');
      } else {
        this.enable('installationUnit');
      }
    });
    
    this.loading = false;
  }

  createForm(location?: UdeuLocation): FormGroup<any> {
    return this._formBuilder.group({
      id: [location?.id],
      deposit: [location?.deposit, [Validators.required]],
      installationUnit: [{ value: location?.installationUnit, disabled: true}, [Validators.minLength(3), Validators.pattern(/^[0-9]\d*$/)]],
      document: [{value: location?.document, disabled: true}],
      signaturaDocE: [{value: location?.signaturaDocE, disabled: true}],
      signatura: [{value: location?.signatura, disabled: true}],
    });
  }

  depositChange() {
    this.formGroup.get('installationUnit').setValue('');
    this.formGroup.get('installationUnit').updateValueAndValidity();

    this.formGroup.get('signaturaDocE').setValue('');
    this.formGroup.get('signaturaDocE').updateValueAndValidity();
  }

  enable(controlName : string) {
    const control = this.formGroup.get(controlName);
    control.disabled ? control.enable() : null;
  }

  disable(controlName : string) {
    const control = this.formGroup.get(controlName);
    control.enabled ? control.disable() : null;
  }

  addLocation() {
    const location = new UdeuLocation();
    location.deposit = this.formGroup.get('deposit').value;
    location.installationUnit = this.formGroup.get('installationUnit').value;
    location.document = this.formGroup.get('document').value;
    location.signaturaDocE = this.formGroup.get('signaturaDocE').value;

    this.addLocationForm(location);
    this.resetForm();
  }

  addLocationForm(location?: UdeuLocation) {
    const form = this.createForm(location);
    this.formArray.push(form);
  }

  removeLocationForm(index: number) {
    this.formArray.removeAt(index);
  }

  resetForm() {
    this.formGroup.reset();
    this.formGroup.setErrors(null);
    this.formGroup.markAsUntouched();
  }

  delete(index: number) {
    const location: UdeuLocation  = this.formArray.at(index).getRawValue();
    if(location.installationUnit) {
      this.formArray.getRawValue().forEach((loc: UdeuLocation, index: number) => {
        if(loc.deposit === location.deposit && loc.installationUnit === location.installationUnit) {
          let document = Number(loc.document);
          if(document >  Number(location.document)) {
            document = document -1;
            this.formArray.at(index).get('document').setValue(document.toString().padStart(3, '0'));
          }
        }
      });
  
      const locationForm: UdeuLocation = this.formGroup.getRawValue();
      if(locationForm.document) {
        if(locationForm.deposit === location.deposit && locationForm.installationUnit === location.installationUnit) {
          const document = Number(locationForm.document) -1;
          this.formGroup.get('document').setValue(document.toString().padStart(3, '0'));
        }
      }
    }

    this.removeLocationForm(index);
  }

  get formArray(): FormArray {
    return this.group.get(this.controlName) as FormArray;
  }

  formArrayGroup (index: number): FormGroup {
    return this.formArray.at(index) as FormGroup;
  }

  async getDocuments() {
    const installationUnit = this.formGroup.get('installationUnit').value;
    if (typeof installationUnit === 'string' && installationUnit.length >= 3) {
      this.loadingDocuments = true;
      try {
        const deposit = this.formGroup.get('deposit').value;
        const results: NodePaging = await this.recordService.getLocatedDocuments(deposit.key, installationUnit);
        let documentsLength = results.list?.pagination.totalItems;
        const newLocations = this.formArray.getRawValue()?.filter((location: UdeuLocation) => !location.id && (location.deposit as KeyValue<string,string>).key === deposit.key && location.installationUnit === installationUnit);
        documentsLength = documentsLength + newLocations.length;
        this.formGroup.get('document').setValue((documentsLength+1).toString().padStart(3, '0'));
      } catch (err) {
        console.error(err);
        this.store.dispatch(new SnackbarWarningAction('No se pudo obtener los expedientes.'));
      } finally {
        this.loadingDocuments = false;
      }
    }
  }

}
